import { createClient } from '@supabase/supabase-js';
import { createCrossSubdomainAuthStorage } from './auth-storage';
import { 
    GeneratedImage, CreditTransaction, PromoCode, PromoRedemption, AdminAction, RedeemPromoResult,
    EventLog, EventType, UserWithStatus, UserTimeline, IPAnalytics, DomainAnalytics,
    UserRiskStatus, ControlStatus, DomainControlStatus, RateLimitCheck,
    Generation, GenerationStatus, CreditLedgerEntry, GenerationsFilters, SalesMetrics
} from './types';

export { createCrossSubdomainAuthStorage } from './auth-storage';
export { adoptSharedSession } from './session-sync';

/** Supabase-js aborts overlapping auth/RPC fetches with no reason string. */
export function isAbortError(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false;
    const name = 'name' in err ? String((err as { name?: unknown }).name) : '';
    const message = 'message' in err ? String((err as { message?: unknown }).message) : '';
    return name === 'AbortError' || message.includes('signal is aborted');
}

if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        if (isAbortError(event.reason)) event.preventDefault();
    });
}

// Explicitly access environment variables to ensure Vite's static replacement works correctly.
// Dynamic access (e.g. env[key]) often fails in production builds because the bundler cannot tree-shake or inline the values.

const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || process.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY || '';

const isConfigured = !!(supabaseUrl && supabaseAnonKey && supabaseUrl.startsWith('http') && !supabaseUrl.includes('placeholder'));

if (!isConfigured) {
  console.warn(
    'Supabase environment variables missing or invalid. Authentication will be disabled.\n' +
    'Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your environment (e.g. .env.local or Vercel Settings).'
  );
}

const validUrl = isConfigured ? supabaseUrl : 'https://placeholder.supabase.co';
const validKey = isConfigured ? supabaseAnonKey : 'placeholder';

export const supabase = createClient(validUrl, validKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: createCrossSubdomainAuthStorage(),
  },
  global: {
    headers: {},
  },
});

// --- HELPER FUNCTIONS ---

const base64ToBlob = (base64: string): Blob => {
  const parts = base64.split(';base64,');
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);
  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }
  return new Blob([uInt8Array], { type: contentType });
};

export const uploadImageToStorage = async (
  userId: string, 
  imageData: File | Blob | string, 
  type: 'original' | 'processed',
  customId?: string
): Promise<string | null> => {
    if (!isConfigured) return null;
    
    try {
        let blob: Blob;
        let ext = 'jpg';
        
        if (imageData instanceof File) {
             blob = imageData;
             ext = imageData.name.split('.').pop() || 'jpg';
        } else if (typeof imageData === 'string') {
             blob = base64ToBlob(imageData);
             if (imageData.startsWith('data:image/png')) ext = 'png';
             else if (imageData.startsWith('data:image/webp')) ext = 'webp';
        } else {
             blob = imageData;
        }

        // 1. Generate Unique Filename using customId (if provided for linking) or randomUUID
        const uniqueId = customId || crypto.randomUUID();
        
        // 2. Determine Folder Path based on type
        // STRICT STRUCTURE: [userId]/[folder]/[uuid].[ext]
        const folder = type === 'original' ? 'originals' : 'results';
        const filename = `${userId}/${folder}/${uniqueId}.${ext}`;

        // 3. Upload to 'images' bucket with Upsert: true
        const { data, error } = await supabase.storage
            .from('images')
            .upload(filename, blob, {
                cacheControl: '3600',
                upsert: true
            });

        if (error) {
            console.error('Storage Upload Error:', error);
            throw error;
        }

        // 4. Construct specific Public URL format dynamically
        const { data: { publicUrl } } = supabase.storage
            .from('images')
            .getPublicUrl(filename);
            
        return publicUrl;
    } catch (err) {
        console.error("Failed to upload image:", err);
        throw err;
    }
};

export const getUserImages = async (userId: string): Promise<GeneratedImage[]> => {
    if (!isConfigured) return [];
    
    const { data, error } = await supabase
        .from('images')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
        
    if (error) {
        console.error("Fetch Images Error:", error);
        return [];
    }
    return data as GeneratedImage[];
};

export const getUserTransactions = async (userId: string): Promise<CreditTransaction[]> => {
    if (!isConfigured) return [];
    
    const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(100);
        
    if (error) {
        console.error("Fetch Transactions Error:", error);
        return [];
    }
    return data as CreditTransaction[];
};

export const refundCredit = async (userId: string) => {
    if (!isConfigured) return;
    try {
        await supabase.rpc('refund_credit', { user_id: userId });
        console.log("Credit refunded successfully.");
    } catch (err) {
        console.error("Failed to refund credit:", err);
    }
};

/**
 * Store pending marketing consent before email confirmation
 * This is picked up by the handle_new_user trigger when user confirms
 */
export const storePendingMarketingConsent = async (email: string): Promise<void> => {
    if (!isConfigured) return;
    try {
        await supabase
            .from('pending_marketing_consent')
            .upsert({ email }, { onConflict: 'email' });
    } catch (err) {
        console.error('Failed to store pending marketing consent:', err);
    }
};

/**
 * Process image using server-side edge function
 * Handles generation tracking, credit consumption, and refunds
 */
export const processImageServerSide = async (
    imageBase64: string,
    prompt: string,
    aspectRatio: string = '1:1',
    action: 'declutter' | 'upscale' = 'declutter',
    userId?: string,
    userEmail?: string
): Promise<string> => {
    if (!isConfigured) {
        throw new Error('Supabase not configured');
    }

    const { data, error } = await supabase.functions.invoke('process-image', {
        body: {
            imageBase64,
            prompt,
            aspectRatio,
            action,
            userId,
            userEmail
        }
    });

    if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Server processing failed');
    }

    if (!data?.success || !data?.imageBase64) {
        throw new Error(data?.error || 'No image returned from server');
    }

    return data.imageBase64;
};

// =========================================
// PROMO CODE FUNCTIONS
// =========================================

export const redeemPromoCode = async (code: string): Promise<RedeemPromoResult> => {
    if (!isConfigured) {
        return { success: false, error: 'NOT_AUTHENTICATED' };
    }

    try {
        const { data, error } = await supabase.rpc('redeem_promo_code', {
            p_code: code
        });

        if (error) {
            console.error('Redeem promo error:', error);
            return { success: false, error: 'INVALID_CODE' };
        }

        return data as RedeemPromoResult;
    } catch (err) {
        console.error('Failed to redeem promo code:', err);
        return { success: false, error: 'INVALID_CODE' };
    }
};

export const getPromoCodes = async (): Promise<PromoCode[]> => {
    if (!isConfigured) return [];

    const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false });

    if (error) {
        console.error('Fetch promo codes error:', error);
        return [];
    }

    return data as PromoCode[];
};

export const getPromoRedemptions = async (): Promise<PromoRedemption[]> => {
    if (!isConfigured) return [];

    const { data, error } = await supabase
        .from('promo_redemptions')
        .select('*')
        .order('redeemed_at', { ascending: false })
        .limit(100);

    if (error) {
        console.error('Fetch promo redemptions error:', error);
        return [];
    }

    return data as PromoRedemption[];
};

// =========================================
// ADMIN FUNCTIONS
// =========================================

export const checkAdminStatus = async (userId: string): Promise<boolean> => {
    if (!isConfigured) return false;

    try {
        const { data, error } = await supabase.rpc('is_admin', {
            check_user_id: userId
        });

        if (error) {
            console.error('Check admin status error:', error);
            return false;
        }

        return data === true;
    } catch (err) {
        console.error('Failed to check admin status:', err);
        return false;
    }
};

export const grantBonusCredits = async (
    targetUserId: string,
    amount: number,
    reason: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) {
        return { success: false, error: 'NOT_CONFIGURED' };
    }

    try {
        const { data, error } = await supabase.rpc('admin_grant_bonus_credits', {
            p_target_user_id: targetUserId,
            p_amount: amount,
            p_reason: reason
        });

        if (error) {
            console.error('Grant bonus credits error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to grant bonus credits:', err);
        return { success: false, error: err.message };
    }
};

export const createPromoCode = async (
    code: string,
    credits: number,
    maxUses: number | null = null,
    expiresAt: string | null = null,
    description: string | null = null
): Promise<{ success: boolean; error?: string; promo_id?: string }> => {
    if (!isConfigured) {
        return { success: false, error: 'NOT_CONFIGURED' };
    }

    try {
        const { data, error } = await supabase.rpc('admin_create_promo_code', {
            p_code: code,
            p_credits: credits,
            p_max_uses: maxUses,
            p_expires_at: expiresAt,
            p_description: description
        });

        if (error) {
            console.error('Create promo code error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to create promo code:', err);
        return { success: false, error: err.message };
    }
};

export const updatePromoCode = async (
    promoId: string,
    active?: boolean,
    maxUses?: number | null,
    expiresAt?: string | null,
    description?: string | null
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) {
        return { success: false, error: 'NOT_CONFIGURED' };
    }

    try {
        const { data, error } = await supabase.rpc('admin_update_promo_code', {
            p_promo_id: promoId,
            p_active: active,
            p_max_uses: maxUses,
            p_expires_at: expiresAt,
            p_description: description
        });

        if (error) {
            console.error('Update promo code error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to update promo code:', err);
        return { success: false, error: err.message };
    }
};

export const getAdminActions = async (): Promise<AdminAction[]> => {
    if (!isConfigured) return [];

    const { data, error } = await supabase
        .from('admin_actions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

    if (error) {
        console.error('Fetch admin actions error:', error);
        return [];
    }

    return data as AdminAction[];
};

export const searchUsers = async (searchTerm: string): Promise<any[]> => {
    if (!isConfigured) return [];

    const { data, error } = await supabase
        .from('profiles')
        .select('id, email, credits')
        .ilike('email', `%${searchTerm}%`)
        .limit(20);

    if (error) {
        console.error('Search users error:', error);
        return [];
    }

    return data;
};

// =========================================
// BEHAVIOR MONITORING FUNCTIONS
// =========================================

/**
 * Log an event to the event_logs table
 */
export const logEvent = async (
    eventType: EventType,
    userId?: string | null,
    userEmail?: string | null,
    ipAddress?: string | null,
    userAgent?: string | null,
    metadata?: Record<string, any>
): Promise<string | null> => {
    if (!isConfigured) return null;

    try {
        const { data, error } = await supabase.rpc('log_event', {
            p_event_type: eventType,
            p_user_id: userId || null,
            p_user_email: userEmail || null,
            p_ip_address: ipAddress || null,
            p_user_agent: userAgent || null,
            p_metadata: metadata || {}
        });

        if (error) {
            if (!isAbortError(error)) console.error('Log event error:', error);
            return null;
        }

        return data;
    } catch (err) {
        if (!isAbortError(err)) console.error('Failed to log event:', err);
        return null;
    }
};

/**
 * Get activity feed (admin only)
 */
export const getActivityFeed = async (
    limit: number = 100,
    offset: number = 0,
    eventType?: string,
    userId?: string,
    ipAddress?: string
): Promise<{ success: boolean; total?: number; events?: EventLog[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_activity_feed', {
            p_limit: limit,
            p_offset: offset,
            p_event_type: eventType || null,
            p_user_id: userId || null,
            p_ip_address: ipAddress || null
        });

        if (error) {
            console.error('Get activity feed error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get activity feed:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get users with their status (admin only)
 */
export const getUsersWithStatus = async (
    limit: number = 50,
    offset: number = 0,
    search?: string,
    statusFilter?: UserRiskStatus
): Promise<{ success: boolean; total?: number; users?: UserWithStatus[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_users_with_status', {
            p_limit: limit,
            p_offset: offset,
            p_search: search || null,
            p_status_filter: statusFilter || null
        });

        if (error) {
            console.error('Get users with status error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get users with status:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get user timeline (admin only)
 */
export const getUserTimeline = async (userId: string): Promise<{ success: boolean; user?: UserTimeline['user']; events?: EventLog[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_user_timeline', {
            p_user_id: userId
        });

        if (error) {
            console.error('Get user timeline error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get user timeline:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Update user status (admin only)
 */
export const updateUserStatus = async (
    userId: string,
    status: UserRiskStatus,
    reason?: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_update_user_status', {
            p_user_id: userId,
            p_status: status,
            p_reason: reason || null
        });

        if (error) {
            console.error('Update user status error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to update user status:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get IP analytics (admin only)
 */
export const getIPAnalytics = async (limit: number = 50, days: number = 30): Promise<{ success: boolean; ips?: IPAnalytics[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_ip_analytics', {
            p_limit: limit,
            p_days: days
        });

        if (error) {
            console.error('Get IP analytics error:', error);
            return { success: false, error: error.message };
        }

        // Database function returns { success, ips } directly
        return data as { success: boolean; ips?: IPAnalytics[]; error?: string };
    } catch (err: any) {
        console.error('Failed to get IP analytics:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Update IP control (admin only)
 */
export const updateIPControl = async (
    ipAddress: string,
    status: ControlStatus,
    throttleMultiplier: number = 1.0,
    reason?: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_update_ip_control', {
            p_ip_address: ipAddress,
            p_status: status,
            p_throttle_multiplier: throttleMultiplier,
            p_reason: reason || null
        });

        if (error) {
            console.error('Update IP control error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to update IP control:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get domain analytics (admin only)
 */
export const getDomainAnalytics = async (limit: number = 50): Promise<{ success: boolean; domains?: DomainAnalytics[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_domain_analytics', {
            p_limit: limit
        });

        if (error) {
            console.error('Get domain analytics error:', error);
            return { success: false, error: error.message };
        }

        return { success: true, domains: data as DomainAnalytics[] };
    } catch (err: any) {
        console.error('Failed to get domain analytics:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Update domain control (admin only)
 */
export const updateDomainControl = async (
    domain: string,
    status: DomainControlStatus,
    throttleMultiplier: number = 1.0,
    reason?: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_update_domain_control', {
            p_domain: domain,
            p_status: status,
            p_throttle_multiplier: throttleMultiplier,
            p_reason: reason || null
        });

        if (error) {
            console.error('Update domain control error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to update domain control:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Check rate limits for magic link / login
 */
export const checkRateLimits = async (
    email: string,
    ipAddress?: string
): Promise<RateLimitCheck> => {
    if (!isConfigured) return { allowed: true };

    try {
        const { data, error } = await supabase.rpc('check_rate_limits', {
            p_email: email,
            p_ip_address: ipAddress || null
        });

        if (error) {
            console.error('Check rate limits error:', error);
            // Fail open - allow if check fails
            return { allowed: true };
        }

        return data as RateLimitCheck;
    } catch (err) {
        console.error('Failed to check rate limits:', err);
        return { allowed: true };
    }
};

/**
 * Check user status for generation permission
 */
export const checkUserGenerationPermission = async (userId: string): Promise<{ allowed: boolean; reason?: string }> => {
    if (!isConfigured) return { allowed: true };

    try {
        const { data, error } = await supabase.rpc('check_user_status', {
            p_user_id: userId
        });

        if (error) {
            console.error('Check user status error:', error);
            return { allowed: true };
        }

        return {
            allowed: data.allowed,
            reason: data.reason
        };
    } catch (err) {
        console.error('Failed to check user status:', err);
        return { allowed: true };
    }
};

// =========================================
// GENERATIONS & CREDIT LEDGER FUNCTIONS
// =========================================

/**
 * Create a generation record (for tracking)
 */
export const createGenerationRecord = async (
    userId?: string,
    userEmail?: string,
    metadata?: Record<string, any>
): Promise<string | null> => {
    if (!isConfigured) return null;

    try {
        const { data, error } = await supabase.rpc('create_generation', {
            p_user_id: userId || null,
            p_user_email: userEmail || null,
            p_input_asset_path: null,
            p_ip_address: null,
            p_user_agent: navigator?.userAgent || null,
            p_metadata: metadata || {}
        });

        if (error) {
            console.error('Create generation error:', error);
            return null;
        }

        return data;
    } catch (err) {
        console.error('Failed to create generation:', err);
        return null;
    }
};

/**
 * Update a generation record status
 */
export const updateGenerationRecord = async (
    generationId: string,
    status: 'completed' | 'failed',
    creditsUsed?: number,
    errorMessage?: string,
    outputUrl?: string,
    inputUrl?: string
): Promise<void> => {
    if (!isConfigured) return;

    try {
        await supabase.rpc('update_generation_status', {
            p_generation_id: generationId,
            p_status: status,
            p_output_path: outputUrl || null,
            p_credits_used: creditsUsed || 0,
            p_error_message: errorMessage || null,
            p_metadata: null,
            p_input_path: inputUrl || null
        });
    } catch (err) {
        console.error('Failed to update generation:', err);
    }
};

/**
 * Get paginated list of generations (admin only)
 */
export const getGenerations = async (
    filters: GenerationsFilters = {}
): Promise<{ success: boolean; total?: number; generations?: Generation[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_generations', {
            p_limit: filters.limit || 50,
            p_offset: filters.offset || 0,
            p_user_id: filters.userId || null,
            p_user_email: filters.userEmail || null,
            p_status: filters.status || null,
            p_date_from: filters.dateFrom || null,
            p_date_to: filters.dateTo || null
        });

        if (error) {
            console.error('Get generations error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get generations:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get user's generations (admin only)
 */
export const getUserGenerations = async (
    userId: string,
    limit: number = 20
): Promise<{ success: boolean; generations?: Generation[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_user_generations', {
            p_user_id: userId,
            p_limit: limit
        });

        if (error) {
            console.error('Get user generations error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get user generations:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get user's credit ledger (admin only)
 */
export const getUserCreditLedger = async (
    userId: string,
    limit: number = 50
): Promise<{ success: boolean; balance?: number; entries?: CreditLedgerEntry[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_user_credit_ledger', {
            p_user_id: userId,
            p_limit: limit
        });

        if (error) {
            console.error('Get user credit ledger error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get user credit ledger:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Adjust user credits (admin only) - uses immutable ledger
 */
export const adjustUserCredits = async (
    userId: string,
    delta: number,
    reason: string
): Promise<{ success: boolean; new_balance?: number; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_adjust_credits', {
            p_user_id: userId,
            p_delta: delta,
            p_reason: reason
        });

        if (error) {
            console.error('Adjust user credits error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to adjust user credits:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get image URL from storage path
 * For public bucket images, returns public URL
 * For private bucket images, would need signed URL (handled server-side)
 */
export const getImageUrl = (storagePath: string | null): string | null => {
    if (!storagePath) return null;
    
    // If it's already a full URL, return as-is
    if (storagePath.startsWith('http')) {
        return storagePath;
    }
    
    // Assume public bucket 'images'
    const { data } = supabase.storage
        .from('images')
        .getPublicUrl(storagePath);
    
    return data.publicUrl;
};

/**
 * Get signed URL for private image (admin only)
 * Note: This must be called from an edge function for security
 */
export const getSignedImageUrl = async (
    bucket: string,
    path: string,
    expiresIn: number = 3600
): Promise<{ success: boolean; url?: string; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.storage
            .from(bucket)
            .createSignedUrl(path, expiresIn);

        if (error) {
            console.error('Get signed URL error:', error);
            return { success: false, error: error.message };
        }

        return { success: true, url: data.signedUrl };
    } catch (err: any) {
        console.error('Failed to get signed URL:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Admin: Cleanup stuck generations (admin only)
 * Marks generations stuck in 'started' for >threshold minutes as 'failed' and refunds credits
 */
export const cleanupStuckGenerations = async (
    thresholdMinutes: number = 15
): Promise<{ success: boolean; generations_cleaned?: number; credits_refunded?: number; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_cleanup_stuck_generations', {
            p_threshold_minutes: thresholdMinutes
        });

        if (error) {
            console.error('Cleanup stuck generations error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to cleanup stuck generations:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get count of stuck generations (for admin display)
 */
export const getStuckGenerationsCount = async (
    thresholdMinutes: number = 15
): Promise<number> => {
    if (!isConfigured) return 0;

    try {
        const thresholdTime = new Date(Date.now() - thresholdMinutes * 60 * 1000).toISOString();
        const { count, error } = await supabase
            .from('generations')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'started')
            .lt('created_at', thresholdTime);

        if (error) {
            console.error('Get stuck generations count error:', error);
            return 0;
        }

        return count || 0;
    } catch (err) {
        console.error('Failed to get stuck generations count:', err);
        return 0;
    }
};

/**
 * Admin: Get sales metrics (admin only)
 * Returns profitability metrics for the given date range
 */
export const getSalesMetrics = async (
    dateFrom?: string,
    dateTo?: string
): Promise<{ success: boolean; metrics?: SalesMetrics; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_sales_metrics', {
            p_date_from: dateFrom || null,
            p_date_to: dateTo || null
        });

        if (error) {
            console.error('Get sales metrics error:', error);
            return { success: false, error: error.message };
        }

        if (!data?.success) {
            return { success: false, error: data?.error || 'Unknown error' };
        }

        return { success: true, metrics: data.metrics };
    } catch (err: any) {
        console.error('Failed to get sales metrics:', err);
        return { success: false, error: err.message };
    }
};

// =========================================
// WORKSPACE-AWARE CREDIT FUNCTIONS
// =========================================

export interface SmartCreditResult {
    success: boolean;
    workspace_type?: 'personal' | 'team';
    workspace_name?: string;
    team_id?: string;
    balance_after?: number;
    error?: string;
    already_processed?: boolean;
}

export interface WorkspaceInfo {
    team_id: string | null;
    workspace_type: 'personal' | 'team';
    workspace_name: string;
    credits: number;
    role?: string;
}

export const smartConsumeCredit = async (
    userId: string,
    amount: number = 1,
    appType?: string,
    refType?: string,
    refId?: string,
    description?: string
): Promise<SmartCreditResult> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('smart_consume_credit', {
            p_user_id: userId,
            p_amount: amount,
            p_app_type: appType || null,
            p_ref_type: refType || null,
            p_ref_id: refId || null,
            p_description: description || 'Credit consumed',
        });

        if (error) {
            console.error('Smart consume credit error:', error);
            return { success: false, error: error.message };
        }

        return data as SmartCreditResult;
    } catch (err: any) {
        console.error('Failed to consume credit:', err);
        return { success: false, error: err.message };
    }
};

export const getActiveWorkspace = async (): Promise<WorkspaceInfo> => {
    if (!isConfigured) return { team_id: null, workspace_type: 'personal', workspace_name: 'Personal', credits: 0 };

    try {
        const { data, error } = await supabase.rpc('get_my_credit_workspace');
        if (error || !data) return { team_id: null, workspace_type: 'personal', workspace_name: 'Personal', credits: 0 };

        const d = data as Record<string, unknown>;
        return {
            team_id: (d.team_id as string) ?? null,
            workspace_type: (d.workspace_type as 'personal' | 'team') ?? 'personal',
            workspace_name: (d.workspace_name as string) ?? 'Personal',
            credits: typeof d.credits === 'number' ? d.credits : 0,
            role: d.role as string | undefined,
        };
    } catch (err) {
        console.error('Failed to get active workspace:', err);
        return { team_id: null, workspace_type: 'personal', workspace_name: 'Personal', credits: 0 };
    }
};

export { isConfigured };
